(() => {
  if (customElements.get('maya-shipping')) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const STORAGE_KEY = 'maya-ship-zip';

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
    });
    const body = response.status === 204 ? null : await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, body };
  };

  class MayaShipping extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ship-form]');
      this.input = this.querySelector('[data-ship-zip]');
      this.button = this.querySelector('[data-ship-submit]');
      this.msg = this.querySelector('[data-ship-msg]');
      this.list = this.querySelector('[data-ship-rates]');
      this.pdp = this.closest('maya-pdp');
      this.moneyFormat = this.pdp?.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
      this.input.addEventListener('input', () => {
        this.input.value = this.mask(this.input.value);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.calculate();
      });
      try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved) this.input.value = this.mask(saved);
      } catch (error) {
        this.input.value = '';
      }
    }

    mask(value) {
      const digits = value.replace(/\D/g, '').slice(0, 8);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }

    money(cents) {
      const value = (cents / 100).toFixed(2).split('.');
      const whole = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, `${whole},${value[1]}`);
    }

    setState(loading, message, isError) {
      this.button.disabled = loading;
      this.classList.toggle('is-loading', loading);
      this.msg.textContent = message || '';
      this.msg.classList.toggle('is-error', Boolean(isError));
    }

    async lookup(zip) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.erro ? null : data;
      } catch (error) {
        return null;
      }
    }

    async calculate() {
      const zip = this.input.value.replace(/\D/g, '');
      this.list.hidden = true;
      this.list.innerHTML = '';
      if (zip.length !== 8) {
        this.setState(false, 'Digite um CEP válido com 8 números.', true);
        this.input.focus();
        return;
      }
      const variantId = this.pdp?.variant?.id;
      const quantity = this.pdp?.quantity || 1;
      if (!variantId) {
        this.setState(false, 'Selecione uma opção do produto para calcular.', true);
        return;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, zip);
      } catch (error) {
        this.input.dataset.saved = 'false';
      }

      this.setState(true, 'Calculando…');
      const address = await this.lookup(zip);
      if (!address) {
        this.setState(false, 'Não encontramos esse CEP. Confira e tente de novo.', true);
        return;
      }

      let restore = null;
      try {
        restore = await this.stage(variantId, quantity);
        const rates = await this.fetchRates(zip, address.uf);
        this.render(rates, address);
      } catch (error) {
        console.error('Maya shipping failed', error);
        this.setState(false, 'Não foi possível calcular agora. Tente de novo em instantes.', true);
      } finally {
        if (restore) await restore().catch((error) => console.error('Maya shipping restore failed', error));
      }
    }

    async stage(variantId, quantity) {
      const before = await request(`${root()}cart.js`);
      if (!before.ok) throw new Error(`cart.js ${before.status}`);
      const existing = before.body.items.find((item) => item.variant_id === variantId);
      if (existing && existing.quantity >= quantity) return null;
      const added = await request(`${root()}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity - (existing ? existing.quantity : 0) }] }),
      });
      if (!added.ok) throw new Error(added.body?.description || `cart/add ${added.status}`);
      const key = added.body.items?.[0]?.key || added.body.key;
      const original = existing ? existing.quantity : 0;
      return () =>
        request(`${root()}cart/change.js`, {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: original }),
        });
    }

    async fetchRates(zip, province) {
      const params = new URLSearchParams({
        'shipping_address[zip]': `${zip.slice(0, 5)}-${zip.slice(5)}`,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': province,
      });
      const prepared = await request(`${root()}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      if (!prepared.ok && prepared.status !== 202) {
        const detail = prepared.body ? Object.values(prepared.body).flat().join(' ') : '';
        throw new Error(detail || `prepare ${prepared.status}`);
      }
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const result = await request(`${root()}cart/async_shipping_rates.json?${params}`);
        if (result.ok && result.body && result.body.shipping_rates) return result.body.shipping_rates;
        if (!result.ok && result.status !== 202) throw new Error(`async ${result.status}`);
        await wait(500 + attempt * 150);
      }
      throw new Error('timeout');
    }

    days(rate) {
      const range = rate.delivery_days || [];
      if (!range.length) return '';
      const [min, max] = [range[0], range[range.length - 1]];
      if (min === max) return `${min} ${min === 1 ? 'dia útil' : 'dias úteis'}`;
      return `${min} a ${max} dias úteis`;
    }

    render(rates, address) {
      const place = [address.localidade, address.uf].filter(Boolean).join(' - ');
      if (!rates.length) {
        this.setState(false, `Ainda não entregamos em ${place}.`, true);
        return;
      }
      const sorted = [...rates].sort((a, b) => Number(a.price) - Number(b.price));
      sorted.forEach((rate) => {
        const cents = Math.round(Number(rate.price) * 100);
        const item = document.createElement('li');
        item.className = 'maya-ship__rate';
        const info = document.createElement('span');
        info.className = 'maya-ship__rate-info';
        const name = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name;
        info.append(name);
        const days = this.days(rate);
        if (days) {
          const eta = document.createElement('small');
          eta.textContent = days;
          info.append(eta);
        }
        const price = document.createElement('span');
        price.className = `maya-ship__rate-price${cents === 0 ? ' is-free' : ''}`;
        price.textContent = cents === 0 ? 'Grátis' : this.money(cents);
        item.append(info, price);
        this.list.append(item);
      });
      this.list.hidden = false;
      this.setState(false, `Entrega para ${place}`);
    }
  }

  customElements.define('maya-shipping', MayaShipping);
})();
